/**
 * Primitives shared by every APA formatter in the app: trimming, date parsing,
 * contributor parsing and page ranges.
 *
 * These are pieces of text handling, not formatters. The two things an author
 * actually reads — the note under a figure and the entry in the reference list —
 * are built by separate modules (`apaFigureNote.js` and `apaImageReference.js`)
 * that share nothing but the helpers here, because the APA house style writes
 * the same source differently in the two places: surname only and Title Case on
 * the slide, full initials and sentence case in the list.
 */

export const cleanValue = (value) => (typeof value === 'string' ? value.trim() : '');

const APA_ORG_KEYWORDS = [
  'association',
  'institute',
  'department',
  'university',
  'college',
  'ministry',
  'agency',
  'center',
  'centre',
  'organization',
  'organisation',
  'committee',
  'office',
  'foundation',
  'society',
  'channel',
  'archives',
  'corporation',
  'company',
  'inc',
  'ltd',
  'co.'
];

export const normalizeDoi = (doi) => {
  const cleaned = cleanValue(doi);
  if (!cleaned) return '';
  return cleaned.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '');
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_ONLY_RE = /^(\d{4})$/;

export const parseDateOnlyParts = (value) => {
  const match = DATE_ONLY_RE.exec(cleanValue(value));
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  // Guard against invalid calendar dates like 2024-02-31.
  if (
    Number.isNaN(utcDate.getTime())
    || utcDate.getUTCFullYear() !== year
    || utcDate.getUTCMonth() !== month - 1
    || utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day, utcDate };
};

export const parseYear = (dateString) => {
  const cleaned = cleanValue(dateString);
  if (!cleaned) return '';

  const yearOnly = YEAR_ONLY_RE.exec(cleaned);
  if (yearOnly) return yearOnly[1];

  const dateOnly = parseDateOnlyParts(cleaned);
  if (dateOnly) return String(dateOnly.year);

  const parsed = new Date(cleaned);
  if (Number.isNaN(parsed.getTime())) return '';
  return String(parsed.getFullYear());
};

export const formatFullDate = (dateString, yearString) => {
  const cleanedDate = cleanValue(dateString);
  if (cleanedDate) {
    // A record that stores only a year means only a year. Handing "2022" to a
    // date parser invents the first of January.
    if (YEAR_ONLY_RE.test(cleanedDate)) return cleanedDate;
    const dateOnly = parseDateOnlyParts(cleanedDate);
    if (dateOnly) {
      return dateOnly.utcDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
      });
    }

    const parsed = new Date(cleanedDate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  const cleanedYear = cleanValue(yearString);
  return cleanedYear;
};

/**
 * The APA date for a work published on a known day: "2019, June 12".
 * Falls back to the year alone when only a year is known.
 */
export const formatYearMonthDay = (dateString, yearString) => {
  const cleanedDate = cleanValue(dateString);
  const dateOnly = parseDateOnlyParts(cleanedDate);
  if (dateOnly) {
    const monthDay = dateOnly.utcDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
    return `${dateOnly.year}, ${monthDay}`;
  }
  return formatYearOnly(dateString, yearString);
};

export const formatYearOnly = (dateString, yearString) => cleanValue(yearString) || parseYear(dateString) || 'n.d.';

export const formatRetrievalDate = (retrievalDate) => {
  const cleaned = cleanValue(retrievalDate);
  if (!cleaned) return '';

  const dateOnly = parseDateOnlyParts(cleaned);
  if (dateOnly) {
    const formattedDateOnly = dateOnly.utcDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
    return `Retrieved ${formattedDateOnly}, from`;
  }

  const parsed = new Date(cleaned);
  if (Number.isNaN(parsed.getTime())) return '';
  const formatted = parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return `Retrieved ${formatted}, from`;
};

export const ensurePeriod = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return '';
  return /[.!?]$/.test(cleaned) ? cleaned : `${cleaned}.`;
};

export const isOrganizationName = (name) => {
  const cleaned = cleanValue(name);
  if (!cleaned) return false;
  if (cleaned.toUpperCase() === cleaned) return true;
  const lower = cleaned.toLowerCase();
  return APA_ORG_KEYWORDS.some((keyword) => lower.includes(keyword));
};

export const splitAuthorList = (rawAuthor = '') => {
  const cleaned = cleanValue(rawAuthor);
  if (!cleaned) return [];
  return cleaned
    .split(/\s*(?:;|&| and )\s*/i)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

export const parseContributor = (entry = '') => {
  const trimmed = cleanValue(entry);
  if (!trimmed) return null;
  if (isOrganizationName(trimmed)) {
    return { name: trimmed, isOrganization: true };
  }
  if (trimmed.includes(',')) {
    const [last, rest] = trimmed.split(',');
    const first = cleanValue(rest).split(/\s+/)[0] || '';
    return {
      lastName: cleanValue(last),
      firstName: first,
      isOrganization: false
    };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { lastName: parts[0], isOrganization: false };
  }
  return {
    firstName: parts[0],
    lastName: parts[parts.length - 1],
    isOrganization: false
  };
};

const normalizeContributorEntry = (contributor) => {
  const name = cleanValue(contributor?.name || contributor?.organization || '');
  const firstName = cleanValue(contributor?.firstName || '');
  const lastName = cleanValue(contributor?.lastName || '');
  if (name) return { name, isOrganization: true };
  if (firstName || lastName) return { firstName, lastName, isOrganization: false };
  return null;
};

const lower = (value) => cleanValue(value).toLowerCase();

/**
 * Is this contributor already the person the author fields name?
 *
 * Surname match, plus a first initial that agrees where both sides have one —
 * so "Whitaker, Harold" and "Whitaker, H." are one author, while "Halas, John"
 * and "Halas, Peter" stay two. An organization counts as a match when its name
 * contains the surname, which is how a group author that has been split into
 * first/last name parts for display finds its way back to itself.
 */
const isSameContributor = (contributor, named) => {
  const surname = lower(named.lastName);
  if (!surname) return false;
  if (contributor.isOrganization) return lower(contributor.name).includes(surname);
  if (lower(contributor.lastName) !== surname) return false;
  const a = lower(named.firstName)[0];
  const b = lower(contributor.firstName)[0];
  return !a || !b || a === b;
};

/**
 * Everyone credited on a source, in the order they should be listed.
 *
 * The editor offers a single "Author first name / Author last name" pair and no
 * way at all to see or edit `contributors`, which only ever arrives from a JSON
 * import or the saved-citation library. So a citation that carried contributors
 * used to swallow the author fields whole: an author could type a name into the
 * one box the dialog gave them, watch the preview ignore it, and have no way to
 * find out why. The named author is now the first author, and the contributors
 * are the ones after them — deduped, so a list that already includes that
 * person does not gain them twice.
 */
export const normalizeContributors = (meta = {}) => {
  const listed = (Array.isArray(meta.contributors) ? meta.contributors : [])
    .map(normalizeContributorEntry)
    .filter(Boolean);

  const lastName = cleanValue(meta.authorLastName);
  const firstName = cleanValue(meta.authorFirstName);
  const named = (firstName || lastName)
    ? { firstName, lastName, isOrganization: false }
    : null;

  if (listed.length) {
    if (!named) return listed;
    if (listed.some((contributor) => isSameContributor(contributor, named))) return listed;
    return [named, ...listed];
  }

  const author = cleanValue(meta.author);
  if (author) {
    const parsed = splitAuthorList(author)
      .map((entry) => parseContributor(entry))
      .filter(Boolean);
    return parsed;
  }
  if (named) return [named];
  return [];
};

/** The surname alone (or the organization's name), which is all a slide note shows. */
export const contributorSurname = (contributor = {}) => (contributor.isOrganization
  ? cleanValue(contributor.name)
  : (cleanValue(contributor.lastName) || cleanValue(contributor.firstName)));

/** "Perkins, L." — the reference list's form, initials and all. */
export const formatReferenceAuthor = (meta = {}) => {
  const contributors = normalizeContributors(meta);
  if (!contributors.length) return '';
  const formatted = contributors.map((contributor) => {
    if (contributor.isOrganization) return contributor.name || '';
    const lastName = cleanValue(contributor.lastName);
    const firstName = cleanValue(contributor.firstName);
    const initial = firstName ? `${firstName[0].toUpperCase()}.` : '';
    return [lastName, initial].filter(Boolean).join(', ').trim();
  }).filter(Boolean);
  if (!formatted.length) return '';
  if (formatted.length > 20) {
    const firstGroup = formatted.slice(0, 19).join(', ');
    const last = formatted[formatted.length - 1];
    return `${firstGroup}, … ${last}`;
  }
  if (formatted.length === 1) return formatted[0];
  return `${formatted.slice(0, -1).join(', ')}, & ${formatted[formatted.length - 1]}`;
};

/** "Smith & Jones", "Smith et al." — the parenthetical in-text form. */
export const formatInTextAuthor = (meta = {}) => {
  const contributors = normalizeContributors(meta);
  if (!contributors.length) return '';
  const names = contributors.map(contributorSurname).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names[0]} et al.`;
};

export const formatPageSegment = (pageValue) => {
  const cleaned = cleanValue(pageValue);
  if (!cleaned) return '';
  const normalized = cleaned.replace(/^p{1,2}\.?\s*/i, '').trim();
  if (!normalized) return '';
  const isRange = /[-–—]/.test(normalized);
  return `${isRange ? 'pp.' : 'p.'} ${normalized}`;
};

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

export const escapeApaHtml = (value) => String(value == null ? '' : value)
  .replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

const isSafeHref = (href) => /^https?:\/\//i.test(cleanValue(href));

/**
 * Fold neighbouring plain segments into one, so a formatter can push text in
 * small pieces without an exporter emitting a run per piece.
 */
export const mergeSegments = (segments = []) => (segments || [])
  .filter((segment) => segment && segment.text)
  .reduce((merged, segment) => {
    const previous = merged[merged.length - 1];
    const isPlain = !segment.italic && !isSafeHref(segment.href);
    const previousIsPlain = previous && !previous.italic && !isSafeHref(previous.href);
    if (previous && isPlain && previousIsPlain) {
      previous.text += segment.text;
      return merged;
    }
    merged.push({ ...segment });
    return merged;
  }, []);

/**
 * One rendering of the segment list every APA formatter returns.
 *
 * A segment is `{ text, italic?, href? }`. Only http(s) links become anchors —
 * every other value is escaped text, so a licence string typed by a teacher can
 * never introduce markup.
 */
export const segmentsToHtml = (segments = [], options = {}) => (segments || [])
  .map((segment) => {
    const text = escapeApaHtml(segment?.text || '');
    if (!text) return '';
    const wrapped = segment?.italic ? `<em>${text}</em>` : text;
    if (!isSafeHref(segment?.href)) return wrapped;
    const linkClass = options.linkClass ? ` class="${escapeApaHtml(options.linkClass)}"` : '';
    return `<a href="${escapeApaHtml(segment.href)}" target="_blank" rel="noreferrer"${linkClass}>${wrapped}</a>`;
  })
  .join('');

/**
 * Turn the URL a reference entry ends with into a real link.
 *
 * A reference entry carries its address as ordinary text, because that is how
 * APA prints it. This finds that address in the segments and gives it an href,
 * appending it when the entry does not already contain it.
 */
export const linkifySegments = (segments = [], link) => {
  const url = cleanValue(link);
  if (!url || !isSafeHref(url)) return mergeSegments(segments);

  const linked = [];
  let placed = false;
  (segments || []).forEach((segment) => {
    const text = segment?.text || '';
    const index = placed || segment?.href ? -1 : text.indexOf(url);
    if (index === -1) {
      linked.push(segment);
      return;
    }
    placed = true;
    if (index > 0) linked.push({ ...segment, text: text.slice(0, index) });
    linked.push({ ...segment, text: url, href: url });
    const after = text.slice(index + url.length);
    if (after) linked.push({ ...segment, text: after });
  });

  if (!placed) {
    linked.push({ text: ' ' });
    linked.push({ text: url, href: url });
  }

  return mergeSegments(linked);
};

/** The same segments as plain text, for a renderer that cannot draw a link. */
export const segmentsToText = (segments = []) => (segments || [])
  .map((segment) => segment?.text || '')
  .join('');

/**
 * Segments as PowerPoint/PDF text runs. Both exporters consume the same run
 * shape, so a licence link survives into either file.
 */
export const segmentsToRuns = (segments = [], options = {}) => (segments || [])
  .filter((segment) => segment?.text)
  .map((segment) => ({
    text: segment.text,
    bold: false,
    italic: Boolean(segment.italic),
    underline: false,
    strike: false,
    mono: false,
    href: isSafeHref(segment.href) ? segment.href : (options.href || null)
  }));
